import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FileSpreadsheet, ArrowRight, RefreshCw, Copy, Star, Eye, EyeOff,
  ChevronDown, ChevronLeft, Search, FlaskConical, Beaker, Edit3,
  Save, X, Check, Layers
} from 'lucide-react';
import toast from 'react-hot-toast';
import { superAdminAPI } from '../../services/api';

const UNIT_SYSTEM_LABELS = {
  conventional: { en: 'Conventional (US)', ar: 'وحدات تقليدية', badge: 'bg-blue-100 text-blue-700', examples: 'mg/dL, ng/mL, g/dL' },
  si: { en: 'SI (International)', ar: 'وحدات دولية', badge: 'bg-emerald-100 text-emerald-700', examples: 'mmol/L, µmol/L, nmol/L' },
};

const TemplateManagement = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);

  // Detail view state
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateDetail, setTemplateDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [testsData, setTestsData] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [expandedTests, setExpandedTests] = useState(new Set());

  // Edit state
  const [editingMeta, setEditingMeta] = useState(false);
  const [metaForm, setMetaForm] = useState({});
  const [savingMeta, setSavingMeta] = useState(false);

  // Component edit state
  const [editingComponent, setEditingComponent] = useState(null);
  const [compForm, setCompForm] = useState({});
  const [savingComp, setSavingComp] = useState(false);

  useEffect(() => { fetchTemplates(); }, []);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const res = await superAdminAPI.getTemplates();
      setTemplates(res.data?.data || []);
    } catch {
      toast.error('فشل في تحميل القوالب');
    } finally {
      setLoading(false);
    }
  };

  const handleSeed = async () => {
    setSeeding(true);
    try {
      await superAdminAPI.seedTemplates();
      toast.success('تم تحديث القوالب بنجاح');
      await fetchTemplates();
    } catch {
      toast.error('فشل في تحديث القوالب');
    } finally {
      setSeeding(false);
    }
  };

  const handleSetDefault = async (id) => {
    try {
      await superAdminAPI.updateTemplate(id, { is_default: true });
      toast.success('تم تعيين القالب الافتراضي');
      await fetchTemplates();
    } catch {
      toast.error('فشل في تعيين القالب الافتراضي');
    }
  };

  const handleToggleActive = async (id, currentActive) => {
    try {
      await superAdminAPI.updateTemplate(id, { is_active: !currentActive });
      toast.success(currentActive ? 'تم تعطيل القالب' : 'تم تفعيل القالب');
      await fetchTemplates();
    } catch {
      toast.error('فشل في تحديث حالة القالب');
    }
  };

  const handleDuplicate = async (id) => {
    try {
      await superAdminAPI.duplicateTemplate(id);
      toast.success('تم نسخ القالب بنجاح');
      await fetchTemplates();
    } catch {
      toast.error('فشل في نسخ القالب');
    }
  };

  // ── Detail View ──
  const openDetail = async (template) => {
    setSelectedTemplate(template);
    setDetailLoading(true);
    setSearchQuery('');
    setSelectedCategory('');
    setExpandedTests(new Set());
    try {
      const [detailRes, testsRes] = await Promise.all([
        superAdminAPI.getTemplate(template._id),
        superAdminAPI.getTemplateTests(template._id, { limit: 250 }),
      ]);
      setTemplateDetail(detailRes.data?.data || null);
      setTestsData(testsRes.data?.data || null);
    } catch {
      toast.error('فشل في تحميل تفاصيل القالب');
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => {
    setSelectedTemplate(null);
    setTemplateDetail(null);
    setTestsData(null);
    setEditingMeta(false);
    setEditingComponent(null);
  };

  // ── Filter tests ──
  const filteredTests = useCallback(() => {
    if (!testsData?.tests) return [];
    let tests = testsData.tests;
    if (selectedCategory) {
      tests = tests.filter(t => t.category_name_en === selectedCategory);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      tests = tests.filter(t =>
        (t.name_en && t.name_en.toLowerCase().includes(q)) ||
        (t.name_ar && t.name_ar.includes(q)) ||
        (t.aliases && t.aliases.toLowerCase().includes(q))
      );
    }
    return tests;
  }, [testsData, selectedCategory, searchQuery]);

  const toggleTest = (idx) => {
    setExpandedTests(prev => {
      const next = new Set(prev);
      next.has(idx) ? next.delete(idx) : next.add(idx);
      return next;
    });
  };

  // ── Meta Edit ──
  const startEditMeta = () => {
    setMetaForm({
      name: selectedTemplate.name,
      name_ar: selectedTemplate.name_ar,
      description: selectedTemplate.description,
      description_ar: selectedTemplate.description_ar,
    });
    setEditingMeta(true);
  };

  const saveMeta = async () => {
    setSavingMeta(true);
    try {
      await superAdminAPI.updateTemplate(selectedTemplate._id, metaForm);
      toast.success('تم حفظ التعديلات');
      setEditingMeta(false);
      setSelectedTemplate({ ...selectedTemplate, ...metaForm });
      await fetchTemplates();
    } catch {
      toast.error('فشل في الحفظ');
    } finally {
      setSavingMeta(false);
    }
  };

  // ── Component Edit ──
  const startEditComponent = (test, secIdx, compIdx, comp) => {
    setEditingComponent({ catIndex: test.catIndex, testIndex: test.testIndex, secIdx, compIdx });
    setCompForm({
      unit: comp.unit || '',
      referenceRanges: JSON.parse(JSON.stringify(comp.referenceRanges || [])),
    });
  };

  const saveComponent = async () => {
    setSavingComp(true);
    try {
      const { catIndex, testIndex, secIdx, compIdx } = editingComponent;
      await superAdminAPI.updateTemplateComponent(selectedTemplate._id, {
        catIndex, testIndex, secIndex: secIdx, compIndex: compIdx,
        data: compForm,
      });
      toast.success('تم حفظ المكون');
      setEditingComponent(null);
      // Refresh tests
      const res = await superAdminAPI.getTemplateTests(selectedTemplate._id, { limit: 250 });
      setTestsData(res.data?.data || null);
    } catch {
      toast.error('فشل في حفظ المكون');
    } finally {
      setSavingComp(false);
    }
  };

  const updateRange = (rangeIdx, field, value) => {
    setCompForm(prev => {
      const ranges = [...prev.referenceRanges];
      ranges[rangeIdx] = { ...ranges[rangeIdx], [field]: value === '' ? null : isNaN(value) ? value : Number(value) };
      return { ...prev, referenceRanges: ranges };
    });
  };

  // ═══════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  // ── Detail View ──
  if (selectedTemplate) {
    const tests = filteredTests();
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={closeDetail} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
              <ChevronLeft className="w-5 h-5" />
            </button>
            <div>
              {editingMeta ? (
                <div className="space-y-2">
                  <input value={metaForm.name} onChange={e => setMetaForm({ ...metaForm, name: e.target.value })}
                    className="text-xl font-bold border rounded-lg px-3 py-1 w-80" />
                  <input value={metaForm.name_ar || ''} onChange={e => setMetaForm({ ...metaForm, name_ar: e.target.value })}
                    className="text-sm border rounded-lg px-3 py-1 w-80" dir="rtl" placeholder="الاسم بالعربي" />
                  <div className="flex gap-2">
                    <button onClick={saveMeta} disabled={savingMeta}
                      className="flex items-center gap-1 px-3 py-1 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700">
                      <Save className="w-3.5 h-3.5" /> {savingMeta ? '...' : 'حفظ'}
                    </button>
                    <button onClick={() => setEditingMeta(false)}
                      className="flex items-center gap-1 px-3 py-1 bg-gray-200 rounded-lg text-sm hover:bg-gray-300">
                      <X className="w-3.5 h-3.5" /> إلغاء
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-2">
                    <h1 className="text-xl font-bold text-gray-900">{selectedTemplate.name}</h1>
                    <button onClick={startEditMeta} className="p-1 hover:bg-gray-100 rounded transition-colors">
                      <Edit3 className="w-4 h-4 text-gray-400" />
                    </button>
                  </div>
                  {selectedTemplate.name_ar && (
                    <p className="text-sm text-gray-500" dir="rtl">{selectedTemplate.name_ar}</p>
                  )}
                </>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {UNIT_SYSTEM_LABELS[selectedTemplate.unit_system] && (
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${UNIT_SYSTEM_LABELS[selectedTemplate.unit_system].badge}`}>
                {UNIT_SYSTEM_LABELS[selectedTemplate.unit_system].ar}
              </span>
            )}
          </div>
        </div>

        {/* Search & Filter */}
        <div className="flex gap-3">
          <div className="relative flex-1">
            <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
              placeholder="بحث بالاسم..."
              className="w-full pr-10 pl-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
              dir="rtl"
            />
          </div>
          <select
            value={selectedCategory} onChange={e => setSelectedCategory(e.target.value)}
            className="border border-gray-200 rounded-xl px-4 py-2.5 text-sm bg-white min-w-[200px]"
          >
            <option value="">كل الفئات ({testsData?.total || 0})</option>
            {(testsData?.categories || []).map(cat => (
              <option key={cat.name_en} value={cat.name_en}>{cat.name_ar || cat.name_en} ({cat.test_count})</option>
            ))}
          </select>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-4 gap-3">
          {[
            { label: 'الفئات', value: selectedTemplate.stats?.total_categories, icon: Layers },
            { label: 'التحاليل', value: selectedTemplate.stats?.total_tests, icon: FlaskConical },
            { label: 'المكونات', value: selectedTemplate.stats?.total_components, icon: Beaker },
            { label: 'النطاقات المرجعية', value: selectedTemplate.stats?.total_reference_ranges, icon: FileSpreadsheet },
          ].map((stat, i) => (
            <div key={i} className="bg-white rounded-xl border border-gray-100 p-3 text-center">
              <stat.icon className="w-5 h-5 mx-auto text-gray-400 mb-1" />
              <div className="text-lg font-bold text-gray-900">{stat.value || 0}</div>
              <div className="text-xs text-gray-500">{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Tests List */}
        {detailLoading ? (
          <div className="flex justify-center py-12">
            <RefreshCw className="w-6 h-6 animate-spin text-blue-500" />
          </div>
        ) : (
          <div className="space-y-2">
            {tests.length === 0 && (
              <div className="text-center py-12 text-gray-400">لا توجد نتائج</div>
            )}
            {tests.map((test, idx) => {
              const isExpanded = expandedTests.has(idx);
              return (
                <motion.div key={idx} layout className="bg-white rounded-xl border border-gray-100 overflow-hidden">
                  {/* Test Header */}
                  <button
                    onClick={() => toggleTest(idx)}
                    className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center gap-3 text-right">
                      <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                      <div>
                        <div className="font-medium text-gray-900 text-sm">{test.name_en}</div>
                        <div className="text-xs text-gray-500">{test.name_ar}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-400 bg-gray-50 px-2 py-0.5 rounded">{test.category_name_ar || test.category_name_en}</span>
                      <span className="text-xs text-gray-400">{test.specimen_type}</span>
                    </div>
                  </button>

                  {/* Expanded Content */}
                  <AnimatePresence>
                    {isExpanded && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="border-t border-gray-100"
                      >
                        <div className="p-4 space-y-3">
                          {/* Test Info */}
                          <div className="grid grid-cols-3 gap-3 text-xs text-gray-500">
                            <div><span className="font-medium">Department:</span> {test.department}</div>
                            <div><span className="font-medium">Tube:</span> {test.tube_type}</div>
                            <div><span className="font-medium">TAT:</span> {test.tat_value} {test.tat_unit}</div>
                          </div>

                          {/* Sections & Components */}
                          {(test.sections || []).map((section, secIdx) => (
                            <div key={secIdx} className="border rounded-lg overflow-hidden">
                              <div className="bg-gray-50 px-3 py-1.5 text-xs font-medium text-gray-600">
                                {section.section_name}
                              </div>
                              <div className="divide-y divide-gray-50">
                                {(section.components || []).map((comp, compIdx) => {
                                  const isEditingThis = editingComponent &&
                                    editingComponent.catIndex === test.catIndex &&
                                    editingComponent.testIndex === test.testIndex &&
                                    editingComponent.secIdx === secIdx &&
                                    editingComponent.compIdx === compIdx;

                                  return (
                                    <div key={compIdx} className="px-3 py-2">
                                      <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-2">
                                          <span className="text-sm font-medium text-gray-800">{comp.name}</span>
                                          <span className="text-xs text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">
                                            {comp.result_type}
                                          </span>
                                          {comp.unit && (
                                            <span className="text-xs text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded font-mono">
                                              {comp.unit}
                                            </span>
                                          )}
                                        </div>
                                        {comp.has_reference_range && (
                                          <button
                                            onClick={() => isEditingThis ? setEditingComponent(null) : startEditComponent(test, secIdx, compIdx, comp)}
                                            className={`p-1 rounded transition-colors ${isEditingThis ? 'bg-blue-100 text-blue-600' : 'hover:bg-gray-100 text-gray-400'}`}
                                          >
                                            <Edit3 className="w-3.5 h-3.5" />
                                          </button>
                                        )}
                                      </div>

                                      {/* Reference Ranges */}
                                      {comp.has_reference_range && (comp.referenceRanges || []).length > 0 && !isEditingThis && (
                                        <div className="mt-1.5 overflow-x-auto">
                                          <table className="w-full text-xs">
                                            <thead>
                                              <tr className="text-gray-400">
                                                <th className="text-right pr-2 font-normal">الجنس</th>
                                                <th className="text-right font-normal">العمر</th>
                                                <th className="text-right font-normal">الحد الأدنى</th>
                                                <th className="text-right font-normal">الحد الأعلى</th>
                                              </tr>
                                            </thead>
                                            <tbody>
                                              {comp.referenceRanges.map((r, ri) => (
                                                <tr key={ri} className="text-gray-600">
                                                  <td className="pr-2">{r.gender}</td>
                                                  <td>{r.age_from}-{r.age_to} {r.age_unit === 'days' ? 'يوم' : 'سنة'}</td>
                                                  <td className="font-mono">{r.low_value ?? '-'}</td>
                                                  <td className="font-mono">{r.high_value ?? '-'}</td>
                                                </tr>
                                              ))}
                                            </tbody>
                                          </table>
                                        </div>
                                      )}

                                      {/* Edit Component Form */}
                                      {isEditingThis && (
                                        <div className="mt-2 p-3 bg-blue-50/50 rounded-lg border border-blue-100 space-y-3">
                                          <div className="flex items-center gap-2">
                                            <label className="text-xs font-medium text-gray-600 w-16">الوحدة:</label>
                                            <input
                                              value={compForm.unit}
                                              onChange={e => setCompForm({ ...compForm, unit: e.target.value })}
                                              className="border rounded px-2 py-1 text-sm font-mono w-32"
                                            />
                                          </div>
                                          <div className="space-y-1">
                                            <div className="text-xs font-medium text-gray-600">النطاقات المرجعية:</div>
                                            <table className="w-full text-xs">
                                              <thead>
                                                <tr className="text-gray-400">
                                                  <th className="text-right font-normal">الجنس</th>
                                                  <th className="text-right font-normal">من عمر</th>
                                                  <th className="text-right font-normal">إلى عمر</th>
                                                  <th className="text-right font-normal">وحدة العمر</th>
                                                  <th className="text-right font-normal">الحد الأدنى</th>
                                                  <th className="text-right font-normal">الحد الأعلى</th>
                                                </tr>
                                              </thead>
                                              <tbody>
                                                {compForm.referenceRanges.map((r, ri) => (
                                                  <tr key={ri}>
                                                    <td>
                                                      <select value={r.gender} onChange={e => updateRange(ri, 'gender', e.target.value)}
                                                        className="border rounded px-1 py-0.5 text-xs w-16">
                                                        <option value="Any">Any</option>
                                                        <option value="Male">Male</option>
                                                        <option value="Female">Female</option>
                                                      </select>
                                                    </td>
                                                    <td><input type="number" value={r.age_from ?? ''} onChange={e => updateRange(ri, 'age_from', e.target.value)}
                                                      className="border rounded px-1 py-0.5 text-xs w-14 font-mono" /></td>
                                                    <td><input type="number" value={r.age_to ?? ''} onChange={e => updateRange(ri, 'age_to', e.target.value)}
                                                      className="border rounded px-1 py-0.5 text-xs w-14 font-mono" /></td>
                                                    <td>
                                                      <select value={r.age_unit} onChange={e => updateRange(ri, 'age_unit', e.target.value)}
                                                        className="border rounded px-1 py-0.5 text-xs w-16">
                                                        <option value="years">years</option>
                                                        <option value="days">days</option>
                                                      </select>
                                                    </td>
                                                    <td><input type="number" step="any" value={r.low_value ?? ''} onChange={e => updateRange(ri, 'low_value', e.target.value)}
                                                      className="border rounded px-1 py-0.5 text-xs w-16 font-mono" /></td>
                                                    <td><input type="number" step="any" value={r.high_value ?? ''} onChange={e => updateRange(ri, 'high_value', e.target.value)}
                                                      className="border rounded px-1 py-0.5 text-xs w-16 font-mono" /></td>
                                                  </tr>
                                                ))}
                                              </tbody>
                                            </table>
                                          </div>
                                          <div className="flex gap-2">
                                            <button onClick={saveComponent} disabled={savingComp}
                                              className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs hover:bg-blue-700 disabled:opacity-50">
                                              <Save className="w-3 h-3" /> {savingComp ? 'جاري الحفظ...' : 'حفظ'}
                                            </button>
                                            <button onClick={() => setEditingComponent(null)}
                                              className="flex items-center gap-1 px-3 py-1.5 bg-gray-200 rounded-lg text-xs hover:bg-gray-300">
                                              <X className="w-3 h-3" /> إلغاء
                                            </button>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  // ── List View ──
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">قوالب التحاليل</h1>
          <p className="text-sm text-gray-500 mt-1">إدارة قوالب التحاليل المخبرية وأنظمة الوحدات</p>
        </div>
        <button
          onClick={handleSeed}
          disabled={seeding}
          className="flex items-center gap-2 px-4 py-2.5 bg-gray-100 hover:bg-gray-200 rounded-xl text-sm font-medium transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${seeding ? 'animate-spin' : ''}`} />
          {seeding ? 'جاري التحديث...' : 'تحديث من الملفات'}
        </button>
      </div>

      {/* Template Cards */}
      {templates.length === 0 ? (
        <div className="text-center py-16">
          <FileSpreadsheet className="w-12 h-12 mx-auto text-gray-300 mb-4" />
          <h3 className="text-lg font-medium text-gray-600 mb-2">لا توجد قوالب</h3>
          <p className="text-sm text-gray-400 mb-4">اضغط "تحديث من الملفات" لإنشاء القوالب</p>
          <button onClick={handleSeed} disabled={seeding}
            className="px-6 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
            {seeding ? 'جاري الإنشاء...' : 'إنشاء القوالب'}
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {templates.map(template => {
            const unitInfo = UNIT_SYSTEM_LABELS[template.unit_system] || { ar: template.unit_system, badge: 'bg-gray-100 text-gray-700', examples: '' };
            return (
              <motion.div
                key={template._id}
                whileHover={{ y: -2 }}
                className={`bg-white rounded-2xl border-2 p-6 cursor-pointer transition-all ${
                  template.is_default ? 'border-blue-200 shadow-lg shadow-blue-100/50' : 'border-gray-100 hover:border-gray-200'
                } ${!template.is_active ? 'opacity-60' : ''}`}
                onClick={() => openDetail(template)}
              >
                {/* Top row */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${unitInfo.badge}`}>
                      {unitInfo.ar}
                    </span>
                    {template.is_default && (
                      <span className="flex items-center gap-1 px-2 py-1 bg-amber-50 text-amber-600 rounded-full text-xs">
                        <Star className="w-3 h-3 fill-current" /> افتراضي
                      </span>
                    )}
                    {!template.is_active && (
                      <span className="px-2 py-1 bg-red-50 text-red-500 rounded-full text-xs">معطل</span>
                    )}
                  </div>
                  <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                    {!template.is_default && (
                      <button onClick={() => handleSetDefault(template._id)} title="تعيين كافتراضي"
                        className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors">
                        <Star className="w-4 h-4 text-gray-400" />
                      </button>
                    )}
                    <button onClick={() => handleToggleActive(template._id, template.is_active)}
                      title={template.is_active ? 'تعطيل' : 'تفعيل'}
                      className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors">
                      {template.is_active ? <Eye className="w-4 h-4 text-gray-400" /> : <EyeOff className="w-4 h-4 text-gray-400" />}
                    </button>
                    <button onClick={() => handleDuplicate(template._id)} title="نسخ"
                      className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors">
                      <Copy className="w-4 h-4 text-gray-400" />
                    </button>
                  </div>
                </div>

                {/* Name */}
                <h3 className="font-bold text-gray-900 mb-1">{template.name}</h3>
                {template.name_ar && <p className="text-sm text-gray-500 mb-3" dir="rtl">{template.name_ar}</p>}

                {/* Unit examples */}
                {unitInfo.examples && (
                  <p className="text-xs text-gray-400 font-mono mb-4">{unitInfo.examples}</p>
                )}

                {/* Stats */}
                <div className="grid grid-cols-4 gap-2">
                  {[
                    { label: 'فئات', value: template.stats?.total_categories },
                    { label: 'تحاليل', value: template.stats?.total_tests },
                    { label: 'مكونات', value: template.stats?.total_components },
                    { label: 'نطاقات', value: template.stats?.total_reference_ranges },
                  ].map((s, i) => (
                    <div key={i} className="text-center bg-gray-50 rounded-lg py-2">
                      <div className="text-base font-bold text-gray-800">{s.value || 0}</div>
                      <div className="text-[10px] text-gray-400">{s.label}</div>
                    </div>
                  ))}
                </div>

                {/* Arrow */}
                <div className="flex justify-end mt-4">
                  <ArrowRight className="w-5 h-5 text-gray-300" />
                </div>
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default TemplateManagement;
